import type { ConnectivityPort, ConnectivityStatus } from '@Domain/Ports/Connectivity/ConnectivityPort';

type NavigatorOnlineSource = {
	readonly onLine: boolean;
};

type OnlineOfflineEventTarget = {
	addEventListener(eventName: 'online' | 'offline', listener: () => void): void;
	removeEventListener(eventName: 'online' | 'offline', listener: () => void): void;
};

/** Injected time source: lets `changedAt` be asserted exactly in tests instead of racing the wall clock. */
type Clock = {
	now(): number;
};

/**
 * Reflects `navigator.onLine` without probing the network, and notifies on the `online`/`offline`
 * events of an injected target.
 *
 * `changedAt` is not a re-read of the clock on every call: the platform never reports when
 * `onLine` last flipped, so this adapter tracks that itself. The first reading and every reading
 * that observes a different `isOnline` than the previous one stamps a fresh `changedAt`; a reading
 * that observes no change returns the previous one, unchanged.
 */
class WebConnectivityAdapter implements ConnectivityPort {
	private readonly navigatorOnlineSource: NavigatorOnlineSource;
	private readonly eventTarget: OnlineOfflineEventTarget;
	private readonly clock: Clock;
	private lastReading: ConnectivityStatus | null;

	constructor(
		navigatorOnlineSource: NavigatorOnlineSource = globalThis.navigator,
		eventTarget: OnlineOfflineEventTarget = globalThis.window,
		clock: Clock = { now: () => Date.now() },
	) {
		this.navigatorOnlineSource = navigatorOnlineSource;
		this.eventTarget = eventTarget;
		this.clock = clock;
		this.lastReading = null;
	}

	current(): ConnectivityStatus {
		const isOnline = this.navigatorOnlineSource.onLine;

		if (this.lastReading === null || this.lastReading.isOnline !== isOnline) {
			this.lastReading = { isOnline, changedAt: this.clock.now() };
		}

		return this.lastReading;
	}

	subscribe(listener: (status: ConnectivityStatus) => void): () => void {
		const handleConnectivityEvent = () => {
			listener(this.current());
		};

		this.eventTarget.addEventListener('online', handleConnectivityEvent);
		this.eventTarget.addEventListener('offline', handleConnectivityEvent);

		return () => {
			this.eventTarget.removeEventListener('online', handleConnectivityEvent);
			this.eventTarget.removeEventListener('offline', handleConnectivityEvent);
		};
	}
}

export default WebConnectivityAdapter;
